import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { CircularProgress, Typography, Box } from '@material-ui/core'
import { Auth } from 'aws-amplify'
import config from '../../../../config/runtime'
import { getEquipmentDescription } from '../../EquipmentUtil'
import { isAdmin, isMultipleOrganization } from '../../../../Utils/ServerUtil'

interface EvalItem {
    item: {
        sticker_id: string
    }
}

interface WallOfTextTabProps {
    evalItem: EvalItem
}

interface ApiItemResponse {
    id: number
    icn: string
    html_wall_of_text?: string
}

const useStyles = makeStyles((theme) => ({
    container: {
        padding: theme.spacing(3),
        height: '100%',
        overflow: 'auto',
        backgroundColor: '#f5f5f5',
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '200px',
    },
    errorContainer: {
        padding: theme.spacing(2),
        backgroundColor: '#f8d7da',
        border: '1px solid #f5c6cb',
        borderRadius: '4px',
        color: '#721c24',
    },
    noDataContainer: {
        padding: theme.spacing(2),
        backgroundColor: '#fff3cd',
        border: '1px solid #ffeaa7',
        borderRadius: '4px',
        color: '#856404',
        textAlign: 'center',
    },
  contentWrapper: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: theme.spacing(4),
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    maxWidth: '800px',
    margin: '0 auto',
  },
  header: {
    backgroundColor: '#4f2681',
    color: 'white',
    padding: theme.spacing(2.5),
    borderRadius: '8px',
    marginBottom: theme.spacing(3),
    textAlign: 'center',
    '& h1': {
      margin: 0,
      fontSize: '24px',
      color: 'white',
    },
    '& .id': {
      marginTop: theme.spacing(1),
      fontSize: '14px',
      color: 'white',
      opacity: 0.9,
    },
  },
  htmlContent: {
    fontFamily: 'Arial, sans-serif',
    lineHeight: 1.6,
    color: '#333',
    '& h1, & h2, & h3': {
      color: '#367C2B',
      marginBottom: theme.spacing(2),
    },
        '& ul': {
            margin: '10px 0',
            paddingLeft: theme.spacing(2.5),
            '& li': {
                margin: '8px 0',
                lineHeight: 1.4,
            },
        },
        '& strong': {
            fontWeight: 600,
        },
        '& .section': {
            marginBottom: theme.spacing(2.5),
            border: '1px solid #ddd',
            borderRadius: '5px',
            overflow: 'hidden',
        },
        '& .section-header': {
            backgroundColor: '#367C2B',
            color: 'white',
            padding: '12px 15px',
            fontWeight: 'bold',
            fontSize: '16px',
        },
        '& .section-content': {
            padding: theme.spacing(2),
            '& ul': {
                margin: 0,
                paddingLeft: theme.spacing(2.5),
            },
            '& li': {
                margin: '8px 0',
                lineHeight: 1.4,
            },
        },
        '& .basic-info': {
            backgroundColor: '#f8f9fa',
            padding: theme.spacing(2),
            borderRadius: '5px',
            marginBottom: theme.spacing(2.5),
            borderLeft: '4px solid #367C2B',
            '& ul': {
                margin: '10px 0',
                paddingLeft: theme.spacing(2.5),
            },
            '& li': {
                margin: '5px 0',
                fontWeight: 500,
            },
        },
        '& .nested-list': {
            marginTop: '5px',
            '& ul': {
                listStyleType: 'circle',
            },
        },
        '& .alert': {
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeaa7',
            color: '#856404',
            padding: theme.spacing(2),
            borderRadius: '5px',
            marginTop: theme.spacing(2.5),
        },
        '& .alert-danger': {
            backgroundColor: '#f8d7da',
            borderColor: '#f5c6cb',
            color: '#721c24',
            '& h3': {
                marginTop: 0,
                color: '#721c24',
            },
            '& ul': {
                marginBottom: 0,
            },
        },
        '& .important-note': {
            backgroundColor: '#d1ecf1',
            border: '1px solid #bee5eb',
            color: '#0c5460',
            padding: theme.spacing(2),
            borderRadius: '5px',
            marginTop: theme.spacing(2.5),
        },
        '& .link': {
            color: '#4f2680',
            textDecoration: 'none',
            fontWeight: 'bold',
            '&:hover': {
                textDecoration: 'underline',
            },
        },
        '& .no-data': {
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeaa7',
            color: '#856404',
            padding: theme.spacing(2),
            borderRadius: '5px',
            textAlign: 'center',
        },
    },
}))

function WallOfTextTab({ evalItem }: WallOfTextTabProps) {
    const classes = useStyles()
    const [loading, setLoading] = useState<boolean>(true)
    const [error, setError] = useState<string | null>(null)
    const [htmlContent, setHtmlContent] = useState<string>('')
    const stickerId = evalItem?.item?.sticker_id
    const itemTitle =
        getEquipmentDescription(evalItem?.item || {}) || stickerId || 'Item'

    useEffect(() => {
        const fetchWallOfText = async () => {
            if (!stickerId) {
                setError('No item ID provided')
                setLoading(false)
                return
            }

            try {
                setLoading(true)
                setError(null)

                const url = `${config.IMS_ICN_WALLOFTEXT}/${stickerId}`

                let jwtToken
                try {
                    const session = await Auth.currentSession()
                    jwtToken = session.getAccessToken().getJwtToken();
                } catch (e) {
                    try {
                        const session = await Auth.currentSession()
                        jwtToken = session.getIdToken().getJwtToken();
                    } catch (e2) {
                        setError('Authentication error. Please log in again.')
                        setLoading(false)
                        return
                    }
                }

                const headers: Record<string, string> = {
                    authorization: jwtToken,
                    accept: 'application/json',
                }

                if (isAdmin() || isMultipleOrganization()) {
                    const organization =
                        localStorage['hw.' + window.btoa('organization')]
                    if (organization && window.atob(organization)) {
                        headers['HW-Organization'] = window.atob(organization)
                            ? window.atob(organization)
                            : 'f376c401-6295-4038-a146-e8b52eef6385'
                    }
                }

                // First API call to get the id using ICN
                const firstResponse = await fetch(url, {
                    method: 'GET',
                    headers,
                })

                if (!firstResponse.ok) {
                    throw new Error(
                        `Failed to fetch item by ICN: ${firstResponse.status}`
                    )
                }

                const firstData: ApiItemResponse[] = await firstResponse.json()

                // Check if we got data and extract the id
                if (!firstData || firstData.length === 0) {
                    setError(
                        `Error this item with ICN/STICKER ID ${stickerId} has no wall of text.`
                    )
                    setLoading(false)
                    return
                }

                const itemId = firstData[0]?.id
                if (!itemId) {
                    setError(
                        `Error this item with ICN/STICKER ID ${stickerId} has no wall of text.`
                    )
                    setLoading(false)
                    return
                }

                // Second API call to get the wall of text using the id
                const secondResponse = await fetch(
                    `${config.IMS_ITEM_ID_WALLOFTEXT}/${itemId}`,
                    {
                        method: 'GET',
                        headers,
                    }
                )

                if (!secondResponse.ok) {
                    throw new Error(
                        `Failed to fetch item details: ${secondResponse.status}`
                    )
                }

                const secondData: ApiItemResponse = await secondResponse.json()

                // Extract html_wall_of_text from the response
                const wallOfText = secondData?.html_wall_of_text

                if (!wallOfText) {
                    setError(
                        `Error this item with ICN/STICKER ID ${stickerId} has no wall of text.`
                    )
                } else {
                    setHtmlContent(wallOfText)
                }
            } catch (err: any) {
                console.error('Error fetching wall of text:', err)
                setError(err.message || 'Failed to fetch wall of text')
            } finally {
                setLoading(false)
            }
        }

        fetchWallOfText()
    }, [evalItem?.item?.sticker_id])

    if (loading) {
        return (
            <div className={classes.container}>
                <div className={classes.contentWrapper}>
                    <div className={classes.loadingContainer}>
                        <CircularProgress />
                        <Typography style={{ marginLeft: 12 }}>
                            Loading Wall Of Text...
                        </Typography>
                    </div>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className={classes.container}>
                <div className={classes.contentWrapper}>
                    <div className={classes.errorContainer}>
                        <Typography variant="h6" gutterBottom>
                            Error Loading Content
                        </Typography>
                        <Typography variant="body2">{error}</Typography>
                    </div>
                </div>
            </div>
        )
    }

    if (!htmlContent || htmlContent.trim() === '') {
        return (
            <div className={classes.container}>
                <div className={classes.contentWrapper}>
                    <div className={classes.errorContainer}>
                        <Typography variant="body2">
                            {`Error this item with ICN/STICKER ID ${stickerId} has no wall of text.`}
                        </Typography>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={classes.container}>
            <div className={classes.contentWrapper}>
        <div className={classes.header}>
          <h1>{itemTitle}</h1>
          {stickerId && (
            <div className="id">{`ICN/STICKER ID: ${stickerId}`}</div>
          )}
        </div>
        <Box
          className={classes.htmlContent}
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
            </div>
        </div>
    )
}

export default WallOfTextTab
